import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { vitrinaPublicUrl } from '../../utils/vitrinaPublicUrl';

interface LandingLayoutProps {
  children: React.ReactNode;
  /** Home hero: bar on brand navy/blue with white links (solo landing principal). */
  headerVariant?: 'default' | 'heroBlue';
  /** When true, skip the app chrome nav (landing marketing usa `MarketingFixedNav`). */
  hideHeader?: boolean;
}

export const LandingLayout: React.FC<LandingLayoutProps> = ({
  children,
  headerVariant = 'default',
  hideHeader = false,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroBlue = headerVariant === 'heroBlue';
  const vitrinaUrl = vitrinaPublicUrl();

  return (
    <div className="bg-gray-50 min-h-screen">
      {!hideHeader && (
      <nav
        className={
          heroBlue
            ? 'sticky top-0 z-50 border-b border-white/15 bg-gradient-to-r from-[#0B1F4A] to-[#1A56DB] shadow-sm'
            : 'bg-white shadow-sm sticky top-0 z-50'
        }
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <a href="/" className="flex-shrink-0">
                <h1 className={`text-2xl font-bold ${heroBlue ? 'text-emerald-400' : 'text-green-600'}`}>📱 Clientelo</h1>
              </a>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a
                href="/negocios"
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
              >
                Negocios
              </a>
              <a
                href="/funciones"
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
              >
                Funcionalidades
              </a>
              <a
                href="/referidos"
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
              >
                Referidos
              </a>
              <a
                href={vitrinaUrl}
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
                target={vitrinaUrl.startsWith('http') ? '_blank' : undefined}
                rel={vitrinaUrl.startsWith('http') ? 'noreferrer' : undefined}
              >
                Vitrina Virtual
              </a>
              <a
                href="#precios"
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
              >
                Precios
              </a>
              <a
                href="#contacto"
                className={heroBlue ? 'text-white/90 hover:text-white transition-colors' : 'text-gray-600 hover:text-green-600 transition-colors'}
              >
                Contacto
              </a>
              {heroBlue && (
                <a
                  href="/registro"
                  className="text-white/95 border border-white/40 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors font-medium"
                >
                  Crear cuenta
                </a>
              )}
              <a href="/login" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-semibold">
                Iniciar Sesión
              </a>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={heroBlue ? 'text-white hover:text-white/80' : 'text-gray-600 hover:text-gray-900'}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div
                className={
                  heroBlue
                    ? 'px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#0a1a42] border-t border-white/15'
                    : 'px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t'
                }
              >
                <a
                  href="/negocios"
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                >
                  Negocios
                </a>
                <a
                  href="/funciones"
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                >
                  Funcionalidades
                </a>
                <a
                  href="/referidos"
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                >
                  Referidos
                </a>
                <a
                  href={vitrinaUrl}
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                  target={vitrinaUrl.startsWith('http') ? '_blank' : undefined}
                  rel={vitrinaUrl.startsWith('http') ? 'noreferrer' : undefined}
                >
                  Vitrina Virtual
                </a>
                <a
                  href="#precios"
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                >
                  Precios
                </a>
                <a
                  href="#contacto"
                  className={`block px-3 py-2 ${heroBlue ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-green-600'}`}
                >
                  Contacto
                </a>
                {heroBlue && (
                  <a
                    href="/registro"
                    className="block px-3 py-2 text-center font-semibold rounded-lg border border-white/40 text-white hover:bg-white/10"
                  >
                    Crear cuenta
                  </a>
                )}
                <a href="/login" className="block px-3 py-2 bg-green-600 text-white rounded-lg text-center font-semibold hover:bg-green-700">
                  Iniciar Sesión
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
      )}

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <a href="/" className="inline-flex items-center gap-3 mb-4">
                <img
                  src="/clientelo-isotipo.png"
                  alt="Clientelo"
                  width={48}
                  height={48}
                  className="h-12 w-12 object-contain"
                />
                <span className="text-xl font-bold text-white">Clientelo</span>
              </a>
              <p className="text-gray-400">Tu Negocio, Conectado</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/negocios" className="hover:text-white">Negocios</a></li>
                <li><a href="/funciones" className="hover:text-white">Funcionalidades</a></li>
                <li><a href="/referidos" className="hover:text-white">Referidos</a></li>
                <li>
                  <a
                    href={vitrinaUrl}
                    className="hover:text-white"
                    target={vitrinaUrl.startsWith('http') ? '_blank' : undefined}
                    rel={vitrinaUrl.startsWith('http') ? 'noreferrer' : undefined}
                  >
                    Vitrina Virtual
                  </a>
                </li>
                <li><a href="#precios" className="hover:text-white">Precios</a></li>
                <li><a href="/demo/gym" className="hover:text-white">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/soporte" className="hover:text-white">Contacto</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/soporte" className="hover:text-white">Centro de ayuda</Link></li>
                <li><Link to="/soporte" className="hover:text-white">Soporte técnico</Link></li>
                <li><Link to="/soporte" className="hover:text-white">Solicitar ayuda</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Clientelo. Todos los derechos reservados.
              </p>
              <a
                href="/legal/"
                className="text-gray-500 hover:text-white text-sm font-medium tracking-wide transition-colors"
              >
                Legal
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
